import { binaryInsert, binarySearch } from "@cervval.js/common/src/tools/collection.tools";
import { compareAsc, isNumber } from "@cervval.js/common/src/tools/number.tools";
import { ItemValueOrFunction, anyReallyAny, compareFields, getItemVOFValue, sortFields } from "@cervval.js/common/src/tools/object.tools";
import { TsSimpleDuration, UnixTimestamp, add, beginOf, closest, durationValue, get, isValid, subtract, valueOf } from "@cervval.js/common/src/tools/timestamps.tools";
import { DayAgendaColumnViewContext } from "../dayAgendaColumnView/dayAgendaColumnView";
import { ColumnUid } from "./dayAgenda.types";
import { ColumnContext, DayAgendaItemContext } from "./dayAgendaContext";
import { DayAgendaModel } from "./dayAgendaModel";
import { AgendaColumnOptions, AgendaItemStyle, MagnetismOptions } from "./dayAgendaOptions";
import { DayAgendaSelectionManager } from "./dayAgendaSelection.helper";

/** Items that are started and not finished at time */
export type ItemContextTimeMap<ItemObject> = { [time: number]: DayAgendaItemContext<ItemObject>[] };

export type TimeMapObject<ItemObject> = {
    /** Items that start on the 'time' in the map */
    startedObjects: DayAgendaItemContext<ItemObject>[],

    /** Items that finish on the 'time' in the map */
    finishedObjects: DayAgendaItemContext<ItemObject>[]
}

/** Items starting of finishing at time */
export type TimeMap<ItemObject = unknown> = { [time: number]: TimeMapObject<ItemObject> };

export const DEFAULT_ITEM_Z_INDEX = 2;// To be drawn over agendaTimescale grid
export const DEFAULT_ITEM_DRAG_Z_INDEX = DEFAULT_ITEM_Z_INDEX + 1;// To be drawn over other items
export const DEFAULT_AGENDA_LAYOUT_Z_INDEX = DEFAULT_ITEM_DRAG_Z_INDEX + 1;// So that items are not drawn over agenda layout (smartList + splitpane separator)

const MIN_Y_RANK = 0;

/** build a context item with default values */
export function buildItemContext<ItemObject, ColumnObject>(context: {
    columnUid: ColumnUid,
    item: ItemObject,
    itemUid: string,
    startTs: UnixTimestamp,
    finishTs: UnixTimestamp
} & Partial<DayAgendaItemContext<ItemObject>>, colOptions: AgendaColumnOptions<ItemObject, ColumnObject>
): DayAgendaItemContext<ItemObject> {
    const itemContext: DayAgendaItemContext<ItemObject> = {
        height: -1,
        concurrentItemsCount: 0,
        subColIndex: 0,
        itemStyle: {} as AgendaItemStyle,
        isResizable: false,
        itemLayerIndex: 0,
        itemStackingPadding: 0,
        ...context
    };
    return itemContext;
}

/**
 * Build items' context from col context. Compute row height from items to display.
 * @param context 
 * @param colContext 
 * @param items 
 * @param agendaModel 
 * @param colOptions 
 * @param agendaSelectionManager
 * @returns Computed items context and col height { itemContexts, rowHeight }
 */
export function buildColContextItems<ItemObject, ColumnObject>(
    context: DayAgendaColumnViewContext,
    colContext: ColumnContext<ItemObject, ColumnObject>,
    items: ItemObject[],
    agendaModel: DayAgendaModel<ItemObject, ColumnObject>,
    colOptions: AgendaColumnOptions<ItemObject, ColumnObject>,
    agendaSelectionManager: DayAgendaSelectionManager<ItemObject> | null
): DayAgendaItemContext<ItemObject>[] {
    const itemContexts: DayAgendaItemContext<ItemObject>[] = [];
    const newItemContextUids: string[] = [];
    for (const item of items) {
        const itemStartTs = agendaModel.getItemStartTs(item);
        const itemFinishTs = agendaModel.getItemFinishTs(item);

        // build context
        const itemUid = agendaModel.getItemUid(item);
        newItemContextUids.push(itemUid);
        const itemContext: DayAgendaItemContext<ItemObject> = {
            item,
            zIndex: DEFAULT_ITEM_Z_INDEX,
            itemUid: itemUid,
            startTs: itemStartTs,
            finishTs: itemFinishTs,
            subColIndex: -1, // To be computed later
            height: -1,// To be computed later
            itemStackingPadding: -1,// To be computed later
            itemStyle: agendaModel.getItemStyle(item, colOptions.defaultItemStyle),
            columnUid: colContext.uid,
            concurrentItemsCount: MIN_Y_RANK,
            isResizable: agendaModel.canResizeItem(item),
            itemLayerIndex: agendaModel.getItemLayerIndex(item)
        };


        // html
        itemContext.itemRender = (iContext: DayAgendaItemContext<ItemObject>, mainDiv: HTMLElement, innerDiv: HTMLElement) => {
            colOptions.itemRender(agendaModel, iContext, mainDiv, innerDiv);

            // Apply selected item style if necessary
            const itemSelected = agendaSelectionManager?.getSelectedData().get(iContext.itemUid);
            if (itemSelected) {
                itemSelected.htmlItem = mainDiv;
            }
        };
        itemContext.itemTooltip = () => agendaModel.getItemTooltipContent(item);
        itemContext.itemTooltipOptions = agendaModel.getItemTooltipOptions?.(item);

        itemContexts.push(itemContext);
    }

    // We need to use item contexts already in col cache to compute accurately the timeMap and the itemContextY
    const computedItemContextsForTimeMap: DayAgendaItemContext<ItemObject>[] = [];
    colContext.itemContexts.forEach((itemContext) => {
        // Filter itemContexts that aren't displayed
        if (itemContext.finishTs <= context.startTs || itemContext.startTs >= context.finishTs) { return; }
        // Filter itemContexts that are re-built
        if (!newItemContextUids.includes(itemContext.itemUid)) {
            computedItemContextsForTimeMap.push(itemContext);
        }
    });
    itemContexts.sort(_generateCompareContextFunction(agendaModel));

    // Compute x margin for contexts
    computeItemsStacking({ agendaModel, itemContexts, computedItemContextsForTimeMap, colContext });
    return itemContexts;
}

/** Compute items stacking using itemContexts */
export function computeItemsStacking<ItemObject, ColumnObject>(params: {
    agendaModel: DayAgendaModel<ItemObject, ColumnObject>,
    itemContexts: DayAgendaItemContext<ItemObject>[],
    computedItemContextsForTimeMap: DayAgendaItemContext<ItemObject>[],
    colContext: ColumnContext<ItemObject, ColumnObject>
}): void {
    const { agendaModel, itemContexts, computedItemContextsForTimeMap } = params;
    // Compute y for contexts
    const itemContextTimeMap = _buildItemContextTimeMap(agendaModel, itemContexts, computedItemContextsForTimeMap);

    // Sort items by startTs, should be done after itemContextTimeMap
    sortFields(itemContexts, "startTs");

    for (const dItems of itemContexts) {
        _computeStackingAndSetItemOffset(itemContextTimeMap, dItems);
    }
}

/**
 * Generates a compare function the compares item contexts by date so that sort will sort properly to compute the TimeMaps
 * if items have same startTs and finishTs, items are compared with agendaModel.compareItemContexts function or by itemUid if no compare function is provided
 * This function creates a binding of the agendaModel.compareItemContexts as third argument to __compareItemContexts()
 */
function _generateCompareContextFunction<ItemObject, ColumnObject>(agendaModel: DayAgendaModel<ItemObject, ColumnObject>): (d1: DayAgendaItemContext<ItemObject>, d2: DayAgendaItemContext<ItemObject>) => number {
    if (agendaModel.compareItemContexts == null) {
        return __compareItemContexts;
    }
    return (d1, d2) => __compareItemContexts(d1, d2, agendaModel.compareItemContexts);
}

/**
 * Compares 2 item contexts by date so that sort will sort properly to compute the TimeMaps
 * if items have same startTs and finishTs, items are compared by itemUid by default or with provided compareItems function
 * * do not use directly, use _generateCompareContextFunction
 */
function __compareItemContexts<ItemObject>(d1: DayAgendaItemContext<ItemObject>, d2: DayAgendaItemContext<ItemObject>, compareItems?: ((d1: DayAgendaItemContext<ItemObject>, d2: DayAgendaItemContext<ItemObject>) => number)): number {
    const itemLayerIndexCompareResult = compareAsc(d1.itemLayerIndex, d2.itemLayerIndex);
    if (itemLayerIndexCompareResult !== 0) { return itemLayerIndexCompareResult; }
    if (compareItems != null) {
        return compareItems(d1, d2);
    }
    return compareFields(d1, d2, "itemUid");
}

/**
 * Build time map of items' context.
 * @param agendaModel 
 * @param itemContexts 
 * @returns Time map
 */
function _buildItemContextTimeMap<
    ItemObject,
    ColumnObject
>(agendaModel: DayAgendaModel<ItemObject, ColumnObject>, itemContexts: DayAgendaItemContext<ItemObject>[], additionalContexts: DayAgendaItemContext<ItemObject>[]): ItemContextTimeMap<ItemObject> {
    const innerAgenda: TimeMap<ItemObject> = {};

    const _compareItemContexts = _generateCompareContextFunction(agendaModel);
    itemContexts.sort(_compareItemContexts);

    // We need to use accurately insert additional contexts to build timeMap
    const allContexts = [...itemContexts];
    for (const aContext of additionalContexts) {
        binaryInsert(allContexts, aContext, false, _compareItemContexts);
    }

    // !Does not filter duplicate items
    for (const itemContext of allContexts) {
        const itemStartTime = itemContext.startTs;
        const itemFinishTime = itemContext.finishTs;
        _recordStart(itemContext, itemStartTime, innerAgenda);
        _recordFinish(itemContext, itemFinishTime, innerAgenda);
    }

    const times: number[] = Object.keys(innerAgenda).map((key: string) => Number(key)).sort((a: number, b: number) => a - b);
    const tmpItems: DayAgendaItemContext<ItemObject>[] = []; // Active items at a time;
    const map: ItemContextTimeMap<ItemObject> = {};

    for (const time of times) {
        const to = innerAgenda[time];

        // Add started objects
        if (to.startedObjects) {
            for (const o of to.startedObjects) {
                binaryInsert(tmpItems, o, false, _compareItemContexts);
            }
        }

        // Remove ended objects
        if (to.finishedObjects) {
            for (const o of to.finishedObjects) {
                const i = binarySearch(tmpItems, o, _compareItemContexts);
                if (i >= 0) { tmpItems.splice(i, 1); }
            }
        }

        // Fill agenda
        map[time] = [...tmpItems]; // clone array
    }
    return map;
}

/**
 * Register o in the timeMap as "starting at time"
 */
function _recordStart<ItemObject = unknown>(o: DayAgendaItemContext<ItemObject>, time: number, timeMap: TimeMap<ItemObject>): void {
    let currentValue: TimeMapObject<ItemObject> = timeMap[time];
    if (!currentValue) {
        currentValue = {
            startedObjects: [],
            finishedObjects: []
        };
        timeMap[time] = currentValue;
    }
    currentValue.startedObjects.push(o);
}

/**
 * Register o in the timeMap as "finishing at time"
 */
function _recordFinish<ItemObject = unknown>(o: DayAgendaItemContext<ItemObject>, time: number, innerAgenda: TimeMap<ItemObject>): void {
    let currentValue: TimeMapObject<ItemObject> = innerAgenda[time];
    if (!currentValue) {
        currentValue = {
            startedObjects: [],
            finishedObjects: []
        };
        innerAgenda[time] = currentValue;
    }
    currentValue.finishedObjects.push(o);
}

/**
 * Compute and set the item y position within the col
 * @returns the updated row height if item is on a new 'sub' line
 */
function _computeStackingAndSetItemOffset<ItemObject>(colItemContextTimeMap: ItemContextTimeMap<ItemObject>, itemContext: DayAgendaItemContext<ItemObject>): void {
    const concurrentItemsOrdered: DayAgendaItemContext<ItemObject>[] = [];
    const time = valueOf(itemContext.startTs);
    const concurrentItems = colItemContextTimeMap?.[time] ?? [];// concurrentItems contains all items that are started and not finished at time

    _computeSubRowIndices(itemContext, concurrentItemsOrdered, concurrentItems);
}

/**
 * Compute sub col indices of items. Used to determine their relative position. Items have different indices if they time intersect.
 * @param itemContext 
 * @param concurrentItemsOrdered 
 * @param concurrentItems 
 * @returns Item order
 */
function _computeSubRowIndices<ItemObject>(itemContext: DayAgendaItemContext<ItemObject>, concurrentItemsOrdered: DayAgendaItemContext<ItemObject>[], concurrentItems: DayAgendaItemContext<ItemObject>[]): void {
    for (const concurrentItem of concurrentItems) {
        if (concurrentItem.itemUid === itemContext.itemUid) { continue; }

        // If item got a itemLayerIndex define, compute his subRowIndice only with items of same itemLayerIndex, exclude others
        // Compute item subRowIndice only with items of same itemLayerIndex, exclude others
        if (concurrentItem.itemLayerIndex !== itemContext.itemLayerIndex) {
            continue;
        }
        if (concurrentItem.subColIndex >= 0) { // Only add the concurrent item if its subRowIndex has been computed
            binaryInsert(concurrentItemsOrdered, concurrentItem, true, (a, b) => a.subColIndex - b.subColIndex);
        }
    }

    // Compute sub col index if necessary
    let sIndex = 0;
    const itemsCount = concurrentItemsOrdered.length + 1;
    for (const indice of concurrentItemsOrdered) {
        if (itemsCount > indice.concurrentItemsCount) {
            indice.concurrentItemsCount = itemsCount;
        }
        itemContext.concurrentItemsCount = itemsCount;

        // Case when an item can fit in a gap between two items
        if (sIndex < indice.subColIndex && sIndex !== indice.subColIndex) {
            break;
        }
        sIndex = indice.subColIndex + 1;
    }

    itemContext.subColIndex = sIndex;// FIXME Calcul pas pertinent, solution temporaire
    itemContext.zIndex = itemContext.subColIndex;
}

/** Return the closest date according to the magnetism options */
export function magnet<
    ItemObject,
    ColumnObject,
>(ts: UnixTimestamp, item: ItemObject | null, col: ColumnObject, magnetismOptions: MagnetismOptions<ItemObject, ColumnObject>, options?: { adjustOnlyStartTs?: boolean, adjustOnlyFinishTs?: boolean }): UnixTimestamp {

    if (!isValid(ts)) { return ts; }
    // If defined, the magnetismFunc is used over any other option
    if (magnetismOptions.magnetismFunc) {
        return magnetismOptions.magnetismFunc(ts, item, col);
    } else {

        // Get the effective magnetism duration => the most constraining step (the biggest duration)
        const precisionDuration = getEffectiveMagnet(item, col, magnetismOptions, options);
        if (!precisionDuration?.unit) { return ts; }
        const precisionUnit = precisionDuration.unit;
        const precisionCount = precisionDuration.unitCount;

        // Get the unit relative number from the date
        const relativeDateValue = get(ts, precisionUnit); // e.g. : unit=days, returns the day of the month (01,02,03,04,...)

        let dateBefore = ts; // Min date to magnet
        let dateAfter = ts; // Max date to magnet
        if (options?.adjustOnlyFinishTs) {// FIXME BGS => OOL there is no difference between the two sides of the if else...
            // Compute the closest date before using the precision magnet count
            // Check if the date we test is already on the magnet anchor
            if (beginOf(ts, precisionUnit).uts !== ts) {
                dateBefore = beginOf(subtract(ts, { [precisionUnit]: relativeDateValue % precisionCount }), precisionUnit).uts;
            }

            // Compute the closest date after using the precision magnet count
            dateAfter = beginOf(add(ts, { [precisionUnit]: precisionCount - relativeDateValue % precisionCount }), precisionUnit).uts;
        } else {
            // Compute the closest date before using the precision magnet count
            // Check if the date we test is already on the magnet anchor
            if (beginOf(ts, precisionUnit).uts !== ts) {
                dateBefore = beginOf(subtract(ts, { [precisionUnit]: relativeDateValue % precisionCount }), precisionUnit).uts;
            }

            // Compute the closest date after using the precision magnet count
            dateAfter = beginOf(add(ts, { [precisionUnit]: precisionCount - relativeDateValue % precisionCount }), precisionUnit).uts;
        }

        // Get the closest date between both previously computed
        const closestTs = closest(ts, [dateBefore, dateAfter]);
        // If valid, return the computed closest date
        if (closestTs && isValid(closestTs)) { return closestTs; }
    }

    return ts;
}

/**
 * Return the most constraining magnet duration (the biggest duration)
 */
function getEffectiveMagnet<
    ItemObject,
    ColumnObject
>(item: ItemObject | null, col: ColumnObject, magnetismOptions: MagnetismOptions<ItemObject, ColumnObject>, options?: { adjustOnlyStartTs?: boolean, adjustOnlyFinishTs?: boolean }): TsSimpleDuration | null {

    if (options?.adjustOnlyStartTs) { // Biggest duration between the ones set for the col and the item's start date
        return getMaxDuration([{ vof: magnetismOptions?.itemStartTsMagnet, item }, { vof: magnetismOptions?.colMagnet, item: col }]);
    } else if (options?.adjustOnlyFinishTs) { // Biggest duration between the ones set for the col and the item's finish date
        return getMaxDuration([{ vof: magnetismOptions?.itemFinishTsMagnet, item }, { vof: magnetismOptions?.colMagnet, item: col }]);
    } else { // Biggest duration between the ones set for the col and the item's start and finish date
        return getMaxDuration([{ vof: magnetismOptions?.itemStartTsMagnet, item }, { vof: magnetismOptions?.itemFinishTsMagnet, item }, { vof: magnetismOptions?.colMagnet, item: col }]);
    }
}

/**
 * Return the biggest duration from an ivof array. 
 * anyReallyAny because can be ItemObject or ColumnObject
 * @param vofsWithItem 
 * @returns null if no duration is defined nor correct
 */
function getMaxDuration(vofsWithItem: { vof: (ItemValueOrFunction<anyReallyAny, TsSimpleDuration> | undefined), item: anyReallyAny | undefined }[]): TsSimpleDuration | null {
    let maxS = 0;
    let maxDuration: TsSimpleDuration | null = null;
    for (const vofWithItem of vofsWithItem) {
        if (!vofWithItem.vof) { continue; }
        const durationVal = getItemVOFValue(vofWithItem.item, vofWithItem.vof, { continueWithNullItem: true });
        if (!durationVal) { continue; }
        const durationSeconds = durationValue(durationVal, "second");
        if (isNumber(durationSeconds) && (maxDuration == null || durationSeconds > maxS)) {
            maxS = durationSeconds;
            maxDuration = durationVal;
        }
    }
    return maxDuration;
}
